import { sortByAssignedTo, sortByCreatedOn, sortByDueDate, sortBySortId, sortByUpdatedOn } from '../../sort/sortUtils';
import { HeaderDO, TaskDO } from '../../../utils/state/model/interfaces/displayObjects';
import { SortOption } from '../../sort/useSortOptions';

/**
 * A locked list forces the manager's order (sortId), ignoring the employee's saved per-device sort.
 * An unset flag — false, or undefined on lists synced before the field existed — passes the saved sort through.
 */
export const resolveTaskSortOption = (
  taskOrderLocked: boolean | undefined,
  savedSortOption: SortOption | undefined
): SortOption | undefined => (taskOrderLocked ? SortOption.MANAGER_ORDER : savedSortOption);

export const prepareQMTasks = async (
  tasks: TaskDO[],
  sortOption: SortOption | undefined,
  isCompletedGroup: boolean
): Promise<(TaskDO | HeaderDO)[]> => {
  return prepareTasks(tasks, sortOption, isCompletedGroup);
};

const sortTasksByOption = async (
  arr: TaskDO[],
  sortOption: SortOption | undefined,
  isCompletedGroup: boolean
): Promise<TaskDO[]> => {
  // A locked list honors the manager's order (sortId) — handled explicitly.
  if (sortOption === SortOption.MANAGER_ORDER) return sortBySortId(arr);

  // For every OTHER (unlocked) sort, seed from a stable, reorder-independent
  // base order (createdOn). Tasks are read from the DB in sortId order and the
  // sorts below are stable, so WITHOUT this, tasks with no/equal due dates keep
  // their sortId order — which means an admin drag-reorder would change an
  // UNLOCKED list's display on mobile. Seeding from createdOn ensures sortId
  // only affects order when the list is locked (MANAGER_ORDER above).
  const base = sortByCreatedOn(arr);
  switch (sortOption) {
    case SortOption.NAME:
      return [...base].sort((a, b) => a.taskName.localeCompare(b.taskName));
    case SortOption.CREATION_DATE:
      return base;
    case SortOption.DUE_DATE:
      return sortByDueDate(base);
    case SortOption.RECENT_ACTIVITY:
      return sortByUpdatedOn(base);
    case SortOption.ASSIGNED_TO:
      return await sortByAssignedTo(base);
    default:
      return isCompletedGroup ? base : sortByDueDate(base, 'ascending');
  }
};

/**
 * From our friends at ChatGPT
 */
function compareArrays(arr1: string[] | undefined, arr2: string[] | undefined): number {
  // Handle cases where one array is undefined
  if (!arr1) {
    return -1;
  }
  if (!arr2) {
    return 1;
  }

  const minLength = Math.min(arr1.length, arr2.length);

  // Compare elements until a difference is found
  for (let i = 0; i < minLength; i++) {
    const comparison = arr1[i].localeCompare(arr2[i]);
    if (comparison !== 0) {
      return comparison; // Return the result of string comparison
    }
    // If elements are equal, continue to the next element
  }

  // If all elements are equal up to the length of the shorter array,
  // the shorter array should come first
  if (arr1.length < arr2.length) {
    return -1; // arr1 should come before arr2
  } else if (arr1.length > arr2.length) {
    return 1; // arr1 should come after arr2
  }

  // If arrays are identical, return 0
  return 0;
}

const flattenArray = async (
  arr: TaskDO[],
  sortOption: SortOption | undefined,
  isCompletedGroup: boolean
): Promise<(TaskDO | HeaderDO)[]> => {
  if (!arr || arr.length === 0) return arr;

  const result: (TaskDO | HeaderDO)[] = [];

  const task = arr[0];
  if (task.header && task.header.length > 0 && task.header.some((header) => header !== '')) {
    const headerObject: HeaderDO = { headers: task.header };
    result.push(headerObject);
  }

  result.push(...(await sortTasksByOption(arr, sortOption, isCompletedGroup)));

  return result;
};

/**
 * Flat ordering for a locked list: sort all tasks by sortId (the manager's order) and emit a
 * HeaderDO only when the header changes along the walk. Labels show inline but never regroup or
 * drive order. Mirrors the header guard used in flattenArray.
 */
const flattenBySortId = (tasks: TaskDO[]): (TaskDO | HeaderDO)[] => {
  const sorted = sortBySortId(tasks);
  const result: (TaskDO | HeaderDO)[] = [];
  let prevHeaderKey: string | null | undefined = undefined;
  for (const task of sorted) {
    const headerKey = task.header?.join('$') || null;
    if (headerKey !== prevHeaderKey) {
      if (task.header && task.header.length > 0 && task.header.some((header) => header !== '')) {
        const headerObject: HeaderDO = { headers: task.header };
        result.push(headerObject);
      }
      prevHeaderKey = headerKey;
    }
    result.push(task);
  }
  return result;
};

export const prepareTasks = async (
  tasks: TaskDO[],
  sortOption: SortOption | undefined,
  isCompletedGroup: boolean
): Promise<(TaskDO | HeaderDO)[]> => {
  if (tasks && tasks.length === 0) return tasks;
  const filteredTasks: TaskDO[] = tasks.filter((t) => t.isCompleted === isCompletedGroup);

  // A locked list renders flat by the manager's sortId order, bypassing the header regroup below.
  if (sortOption === SortOption.MANAGER_ORDER) {
    return flattenBySortId(filteredTasks);
  }

  const groups = new Map<string | null, TaskDO[]>();

  filteredTasks.forEach((task) => {
    const key = task.header?.join('$') || null;

    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)?.push(task);
  });

  const mapEntries = Array.from(groups.entries());

  mapEntries.filter((entry) => {
    //TODO: CC-1546 goes here
    return 1;
  });

  mapEntries.sort((a, b) => {
    const taskA = a[1][0];
    const taskB = b[1][0];
    return compareArrays(taskA.originalHeader, taskB.originalHeader); //Sort by original header to maintain the original order despite inconsistencies in translation
  });

  /*
    flatten the array, 
    generate headers, 
    sort inside of header
  */

  const result: (TaskDO | HeaderDO)[] = [];

  for (const entry of mapEntries) {
    const tasks = entry[1];

    result.push(...(await flattenArray(tasks, sortOption, isCompletedGroup)));
  }

  return result;
};

export const justTheTasks = (arr: (TaskDO | HeaderDO)[]): TaskDO[] => {
  const result: TaskDO[] = [];
  arr.forEach((item) => {
    if ('taskId' in item) result.push(item);
  });
  return result;
};

export const filterTasksAndHeaders = (tasks: (TaskDO | HeaderDO)[], filter: string): (TaskDO | HeaderDO)[] => {
  const filteredTasks = justTheTasks(tasks).filter((task) => {
    return task.taskName.toLowerCase().includes(filter.toLowerCase());
  });

  const filteredTasksAndeHeaders = tasks.filter((task) => {
    if ('taskId' in task) {
      const taskInstance = task as TaskDO;
      const taskNameMatches = taskInstance.taskName.toLowerCase().includes(filter.toLowerCase());
      const headerMatches =
        taskInstance.header &&
        taskInstance.header.some((header) => header.toLowerCase().includes(filter.toLowerCase()));
      return !!taskNameMatches || !!headerMatches;
    }

    if ('headers' in task) {
      const headerInstance = task as HeaderDO;
      return filteredTasks.some((justTask) => justTask.header?.join('') === headerInstance.headers.join(''));
    }

    return false;
  });
  return filteredTasksAndeHeaders;
};

/**
 * Schemes that can execute code when a link is tapped — never valid for an
 * instruction link, and rejected so a stored link can't run script on a
 * worker's device. Mirrors the admin's `instructionsUrl.ts` denylist.
 */
const DANGEROUS_URL_SCHEMES = new Set(['javascript:', 'data:', 'vbscript:']);

/**
 * Validate a task instruction-link URL before it is saved.
 *
 * The link is later rendered as an `<a href>` in the task detail view, so it
 * must be a parseable URL carrying a scheme — this accepts http(s) as well as
 * the non-http forms managers use (mailto:, sms:, tel:, deep links) while
 * rejecting free text and bare hosts like "example.com". The value must also
 * contain no whitespace; a space, tab, or newline inside an href breaks it.
 * Script-bearing schemes (see {@link DANGEROUS_URL_SCHEMES}) are rejected too.
 */
export const isValidURL = (url: string): boolean => {
  if (/\s/.test(url)) return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return !DANGEROUS_URL_SCHEMES.has(parsed.protocol.toLowerCase());
};
